/**
 * Iter XX.285 — Web attribution capture + storage.
 *
 * Latest-known-touch semantics: a page load with attribution
 * parameters becomes the newest stored attribution. A page load
 * without attribution parameters MUST NEVER erase existing storage.
 *
 * Storage:
 *   · localStorage `gully_attribution` (primary, 30d TTL enforced
 *     via `expires_at` inside the payload).
 *   · first-party cookie `gully_attribution` (30d, `SameSite=Lax`,
 *     fallback for browsers that block localStorage).
 *   · localStorage always wins on read; cookie is a rescue.
 *
 * Never throws. Never blocks. Every branch is try/except.
 */
const STORAGE_KEY = "gully_attribution";
const COOKIE_KEY  = "gully_attribution";
const TTL_MS      = 30 * 24 * 60 * 60 * 1000;   // 30 days

// Only these URL params count as attribution signals.
const ATTR_PARAM_KEYS = [
  "fbclid",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
];

function _now() {
  return new Date().toISOString();
}

function _readLocalStorage() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object") return parsed;
    return null;
  } catch { return null; }
}

function _writeLocalStorage(payload) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch { /* private mode / quota / SSR — falls through to cookie */ }
}

function _readCookie() {
  try {
    const raw = document.cookie
      .split(";")
      .map((s) => s.trim())
      .find((s) => s.startsWith(COOKIE_KEY + "="));
    if (!raw) return null;
    const val = decodeURIComponent(raw.slice(COOKIE_KEY.length + 1));
    const parsed = JSON.parse(val);
    if (parsed && typeof parsed === "object") return parsed;
    return null;
  } catch { return null; }
}

function _writeCookie(payload) {
  try {
    const expires = new Date(Date.now() + TTL_MS).toUTCString();
    document.cookie =
      `${COOKIE_KEY}=${encodeURIComponent(JSON.stringify(payload))}; ` +
      `Path=/; Expires=${expires}; SameSite=Lax`;
  } catch { /* noop */ }
}

/**
 * Parse attribution params off a query string. Returns the payload
 * (with all six param keys present, absent values normalised to
 * null) or null when no attribution param is present.
 */
export function parseAttributionFromSearch(search, landingPath) {
  try {
    const q = new URLSearchParams(search || "");
    const hits = {};
    let anyHit = false;
    for (const k of ATTR_PARAM_KEYS) {
      const v = q.get(k);
      if (v != null && String(v).length > 0) {
        hits[k] = String(v).slice(0, 200);
        anyHit = true;
      } else {
        hits[k] = null;
      }
    }
    if (!anyHit) return null;
    return {
      source: "web_landing",
      ...hits,
      landing_path: String(landingPath || "/").slice(0, 200),
      captured_at:  _now(),
    };
  } catch { return null; }
}

/**
 * Read current stored attribution. localStorage primary, cookie
 * fallback. Returns null when neither is populated / usable.
 */
export function readWebAttribution() {
  const ls = _readLocalStorage();
  if (ls) return ls;
  return _readCookie();
}

/**
 * Boot-time capture. Called once from index.js BEFORE React
 * renders. If the current URL has attribution params, they become
 * the latest known attribution and REPLACE any stored payload
 * (approved semantics: latest-touch, not immutable-first-touch).
 * If the URL has no attribution params, existing storage is
 * preserved untouched.
 */
export function captureWebAttributionFromLocation() {
  try {
    const search = (typeof window !== "undefined"
      ? window.location.search : "") || "";
    const path = (typeof window !== "undefined"
      ? window.location.pathname : "") || "/";
    const parsed = parseAttributionFromSearch(search, path);
    if (!parsed) {
      // No attribution params on this visit → do NOT touch storage.
      return null;
    }
    _writeLocalStorage(parsed);
    _writeCookie(parsed);
    return parsed;
  } catch {
    return null;
  }
}

/**
 * Explicit unit-testing / diagnostic helper — clear both stores.
 * Never called from production code. Exported for test only.
 */
export function _clearWebAttribution_forTests() {
  try { window.localStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }
  try {
    document.cookie = `${COOKIE_KEY}=; Path=/; Expires=Thu, 01 Jan 1970 00:00:00 GMT`;
  } catch { /* noop */ }
}

// UTM keys forwarded on session creation (matches Chat.jsx _readAndCacheUtm).
const SESSION_UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
];

/**
 * Iter XX.285c — Pure merge for the /web/session attribution payload.
 *
 * Priority (first-touch wins):
 *   1. `cached` — first-touch UTM cache (localStorage `gully_utm`).
 *   2. `fromUrl` — UTM params on the current page URL.
 *   3. `storedAttr` — latest known web-attribution touch
 *      (`readWebAttribution()`); fills ONLY keys missing from 1+2.
 *
 * This closes the landing → /chat navigation gap: the landing page
 * consumes the tagged URL, then navigates to /chat (stripping UTMs
 * from the URL). Without step 3, sessions created at /chat carry
 * fbclid but no utm_source, making them invisible to attribution
 * filters. The stored touch IS the touch that brought the user to
 * this session, so for session-creation purposes this is first-touch.
 *
 * Pure function — no DOM/storage access. Exported for unit tests;
 * Chat.jsx `_readAndCacheUtm` is the production caller.
 */
export function mergeSessionUtm({ cached, fromUrl, storedAttr }) {
  const c = cached && typeof cached === "object" ? cached : {};
  const u = fromUrl && typeof fromUrl === "object" ? fromUrl : {};
  const merged = Object.keys(c).length > 0 ? { ...c } : { ...u };
  const attr = storedAttr;
  if (attr && typeof attr === "object") {
    for (const k of SESSION_UTM_KEYS) {
      if (attr[k] && !merged[k]) {
        merged[k] = String(attr[k]).slice(0, 120);
      }
    }
    if (attr.fbclid && !merged.fbclid) {
      merged.fbclid = String(attr.fbclid).slice(0, 200);
    }
    if (attr.landing_path && !merged.landing_path) {
      merged.landing_path = String(attr.landing_path).slice(0, 200);
    }
  }
  return merged;
}

/**
 * Iter XX.297 — P-M4 fix: build the first-touch attribution payload for
 * the landing-page session pre-warm (InstantChatLanding.jsx). Mirrors
 * Chat.jsx startSession(): cached (`gully_utm`) + URL UTMs merged
 * first-touch-wins via mergeSessionUtm, plus the anonymous browser_id.
 *
 * Fail-soft — never throws; returns {} when attribution is unavailable.
 * Exported for unit tests; InstantChatLanding is the production caller.
 */
export function buildPrewarmAttribution() {
  try {
    const _UTM_KEYS = ["utm_source", "utm_campaign", "utm_medium", "utm_content", "utm_term"];
    let _cached = {};
    try {
      const _raw = window.localStorage.getItem("gully_utm");
      if (_raw) _cached = JSON.parse(_raw) || {};
    } catch { /* corrupt cache — ignore */ }
    const _params = new URLSearchParams(window.location.search || "");
    const _fromUrl = {};
    for (const _k of _UTM_KEYS) {
      const _v = _params.get(_k);
      if (_v) _fromUrl[_k] = String(_v).slice(0, 120);
    }
    const _merged = mergeSessionUtm({
      cached: _cached,
      fromUrl: _fromUrl,
      storedAttr: readWebAttribution(),
    }) || {};
    try {
      // eslint-disable-next-line global-require
      const { getBrowserId } = require("./browserId");
      const _bid = getBrowserId();
      if (_bid) _merged.browser_id = _bid;
    } catch { /* noop */ }
    return _merged;
  } catch {
    return {};
  }
}
